import type {
  TaxReturn,
  IdentityVerification,
  IdentityVerificationStatus,
  IdentityVerificationErrorCode,
  Signer,
} from '@/_types/models/taxReturns';
import {
  TAX_RETURN_STATUSES,
  type TaxReturnStatus,
} from '@/_constants/tax-return';

export const getTaxReturnStatus = (taxReturn: TaxReturn): TaxReturnStatus => {
  return (
    TAX_RETURN_STATUSES.find((s) => s.value === taxReturn.current_status) ||
    TAX_RETURN_STATUSES[0]
  );
};

export const getUnsignedFormCount = (
  taxReturn: TaxReturn,
  currentUserId?: number,
): number => {
  if (!currentUserId) return 0;

  return taxReturn.e_sign_forms
    .filter((form) => {
      // Filter out forms that are related to the current user
      return (
        form.signatures &&
        form.signatures.some((signature) => {
          return signature.userId === currentUserId;
        })
      );
    })
    .filter((form) => {
      if (form.wetSignaturesRequired) {
        return !form.wetSignatures || form.wetSignatures.length === 0;
      } else {
        return form.signatures.some((signature) => {
          return (
            signature.userId === currentUserId &&
            !signature.signatureData &&
            !signature.isMarkedSigned
          );
        });
      }
    }).length;
};

export const getUnmailedPaperFiledReturnCount = (taxReturn: TaxReturn) => {
  return taxReturn.paper_filed_returns.filter((paperFiledReturn) => {
    return !paperFiledReturn.is_mailed;
  }).length;
};

export const getUnpaidPaymentVouchersCount = (taxReturn: TaxReturn) => {
  return taxReturn.payment_vouchers.filter((paymentVoucher) => {
    return !paymentVoucher.is_paid && !paymentVoucher.is_instruction_page;
  }).length;
};

export const getAggregateVerificationStatus = (
  verifications?: IdentityVerification[],
  signers?: Array<Signer>,
): {
  status: IdentityVerificationStatus;
  error_code?: IdentityVerificationErrorCode | null;
} => {
  // If no verifications exist, return not_started
  if (!verifications?.length) {
    return { status: 'not_started' };
  }

  // If we have fewer verifications than signers, some haven't started
  if (signers?.length && verifications.length < signers.length) {
    return { status: 'requires_input' };
  }

  // Check for any failures first
  const hasFailure = verifications.some(
    (v) => v.error_code || v.status === 'failed',
  );
  if (hasFailure) {
    const errorCode = verifications.find((v) => v.error_code)?.error_code;
    return { status: 'failed', error_code: errorCode };
  }

  // Check for any requiring input
  if (verifications.some((v) => v.status === 'requires_input')) {
    return { status: 'requires_input' };
  }

  // Check for any processing
  if (verifications.some((v) => v.status === 'processing')) {
    return { status: 'processing' };
  }

  // Check if all are verified
  if (verifications.every((v) => v.status === 'verified')) {
    return { status: 'verified' };
  }

  // Check for any canceled
  if (verifications.some((v) => v.status === 'canceled')) {
    return { status: 'canceled' };
  }

  return { status: 'not_started' };
};
